import { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';

type CarouselProps = {
  images?: string[];
  interval?: number;
  fallback?: string;
};

export default function Carousel({ images = [], interval = 5000, fallback }: CarouselProps) {
  const [index, setIndex] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [metaMap, setMetaMap] = useState<Record<string, { width: number; height: number }>>({});
  const imgs = useMemo(() => {
    const list = (Array.isArray(images) ? images : []).map((src) =>
      src.startsWith('http') ? src : src,
    );
    return list.length ? list : ['img_3.png'];
  }, [images]);
  const fallbackSrc = useMemo(() => {
    if (!fallback) return process.env.PUBLIC_URL + '/img_3.png';
    return fallback.startsWith('http') ? fallback : process.env.PUBLIC_URL + '/' + fallback;
  }, [fallback]);
  const [reduce, setReduce] = useState(false);

  // Load hero image intrinsic sizes if available
  useEffect(() => {
    let cancelled = false;
    fetch(process.env.PUBLIC_URL + '/hero.json', { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        if (!j || !Array.isArray(j.items)) return;
        const map: Record<string, { width: number; height: number }> = {};
        for (const it of j.items) {
          if (it && it.src && it.width && it.height) map[it.src] = { width: it.width, height: it.height };
        }
        if (!cancelled) setMetaMap(map);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const buildSets = (rel: string, allowOptimized: boolean) => {
    // Accept absolute URLs, root-absolute paths, or relative asset paths
    const isHttp = /^https?:\/\//i.test(rel);
    if (isHttp) return { avif: '', webp: '', src: rel };
    const isRootAbs = rel.startsWith('/');
    const baseUrl = process.env.PUBLIC_URL || '';
    const clean = isRootAbs ? rel.replace(/^\/+/, '') : rel;
    const dir = clean.split('/').slice(0, -1).join('/');
    const file = clean.split('/').pop() || '';
    const stem = file.replace(/\.[^.]+$/, '');
    const sizes = '(max-width: 760px) 100vw, 50vw';
    const widths = [600, 900, 1200];
    const top = dir.split('/')[0] || '';
    const canOptimize = (allowOptimized && (top === 'hero' || top === 'home' || top === 'gallery')) || top === 'coaches';
    const mk = (ext: string) =>
      widths.map((w) => `${baseUrl}/${dir}/_optimized/${stem}-${w}.${ext} ${w}w`).join(', ');
    const src = isRootAbs ? `/${clean}` : `${baseUrl}/${clean}`;
    if (!canOptimize) return { avif: '', webp: '', sizes, src };
    return { avif: mk('avif'), webp: mk('webp'), sizes, src };
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReduce(!!mq.matches);
    update();
    // Support older browsers
    try {
      (mq as any).addEventListener('change', update);
    } catch {
      (mq as any).addListener?.(update);
    }
    return () => {
      try {
        (mq as any).removeEventListener('change', update);
      } catch {
        (mq as any).removeListener?.(update);
      }
    };
  }, []);

  useEffect(() => {
    if (reduce) return; // respect reduced motion
    timerRef.current = setInterval(() => setIndex((i) => (i + 1) % imgs.length), interval);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [imgs.length, interval, reduce]);

  const goTo = (i: number) => setIndex(((i % imgs.length) + imgs.length) % imgs.length);
  const prev = () => setIndex((i) => (i - 1 + imgs.length) % imgs.length);
  const next = () => setIndex((i) => (i + 1) % imgs.length);

  // Framer Motion typing workaround for img element
  const MImg = motion.img as any;

  return (
    <div className="carousel" role="region" aria-label="Gym Photos">
      <div className="carousel-viewport">
        {imgs.map((rel, i) => {
          const key = rel.startsWith('/') ? rel : '/' + rel;
          const meta = metaMap[key];
          const sets = buildSets(rel, !!meta);
          const imgEl = (
            <MImg
              key={rel}
              src={sets.src}
              alt="Gym training"
              className={`carousel-item ${i === index ? 'active' : ''}`}
              width={meta?.width}
              height={meta?.height}
              loading={i === 0 ? 'eager' : 'lazy'}
              decoding="async"
              fetchpriority={i === 0 ? 'high' : 'auto'}
              onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                if (e.currentTarget.src !== fallbackSrc) e.currentTarget.src = fallbackSrc;
              }}
              whileHover={reduce ? undefined : { scale: 1.01 }}
              transition={{ type: 'spring', stiffness: 200, damping: 24 }}
            />
          );
          if (sets.avif || sets.webp) {
            return (
              <picture key={rel}>
                {sets.avif && <source type="image/avif" srcSet={sets.avif} sizes={sets.sizes} />}
                {sets.webp && <source type="image/webp" srcSet={sets.webp} sizes={sets.sizes} />}
                {imgEl}
              </picture>
            );
          }
          return imgEl;
        })}
      </div>
      <button className="carousel-btn prev" aria-label="Previous" onClick={prev} />
      <button className="carousel-btn next" aria-label="Next" onClick={next} />
      <div className="carousel-dots" role="tablist">
        {imgs.map((_, i) => (
          <button
            key={i}
            className={`dot ${i === index ? 'active' : ''}`}
            role="tab"
            aria-label={`Go to slide ${i + 1}`}
            aria-selected={i === index}
            onClick={() => goTo(i)}
          />
        ))}
      </div>
    </div>
  );
}
